/**
 * The geofence (brief §10).
 *
 * **The server never learns a position.** That is the architecture, not a setting, and this
 * file is where it is paid for: the client fetches the hot cells for a res-5 area (≈ 250 km²,
 * which is "somewhere around Stuttgart"), and every decision about where the person actually
 * is happens on their own device, here.
 *
 * `decide` is a pure function of its inputs — no clock, no geolocation, no storage. That is
 * what makes eight interacting rules testable: a scenario is a literal, not a walk around a
 * city with a stopwatch.
 */

import { greatCircleDistance, UNITS } from 'h3-js';
import { MIN_SIGHTINGS } from './gdi.ts';
import type { Cell } from './h3.ts';
import { isNear } from './h3.ts';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

/** Brief §10, and every one of these is a rule rather than a preference. */
export const SAME_CELL_COOLDOWN_MS = 6 * HOUR;
export const ANY_ALERT_COOLDOWN_MS = 15 * MINUTE;
export const MOVEMENT_THRESHOLD_M = 250;
export const ALERT_RING = 1;

export const THRESHOLD_RANGE = { min: 20, max: 95 } as const;
export const DAILY_CAP_RANGE = { min: 1, max: 20 } as const;

export type AlertSettings = {
  enabled: boolean;
  /** GDI at or above which a cell is worth interrupting somebody for. 20–95. */
  threshold: number;
  /** Alerts per calendar day, device time. 1–20. */
  dailyCap: number;
  /** Local hours. `null` for both means quiet hours are off. Wraps across midnight. */
  quietFrom: number | null;
  quietTo: number | null;
};

export const DEFAULT_ALERT_SETTINGS: AlertSettings = {
  enabled: true,
  threshold: 60,
  dailyCap: 5,
  quietFrom: 22,
  quietTo: 8,
};

export type HotCell = {
  cell: Cell;
  gdi: number;
  lat: number;
  lon: number;
  sightingCount: number;
  /** Epoch milliseconds, or `null` for a cell whose reports have all aged out. */
  lastSightingAt: number | null;
};

export type GeofenceState = {
  /** Epoch ms of the last alert of any kind. */
  lastAlertAt: number | null;
  /** Where the person was standing then, for the movement threshold. */
  lastAlertPosition: { lat: number; lon: number } | null;
  /** Epoch ms per cell, for the six-hour per-cell cooldown. */
  perCell: Record<Cell, number>;
  /** The local calendar day the counter belongs to, `YYYY-MM-DD`. */
  day: string;
  countToday: number;
};

export const EMPTY_STATE: GeofenceState = {
  lastAlertAt: null,
  lastAlertPosition: null,
  perCell: {},
  day: '',
  countToday: 0,
};

export type Alert = {
  cell: Cell;
  gdi: number;
  sightingCount: number;
  lastSightingAt: number | null;
  at: number;
};

export type GeofenceInput = {
  position: { lat: number; lon: number };
  currentCell: Cell;
  hotCells: readonly HotCell[];
  state: GeofenceState;
  settings: AlertSettings;
  /** Epoch milliseconds. */
  now: number;
  /** 0–23 in the device's own time zone. */
  localHour: number;
  /**
   * The device's local calendar day, `YYYY-MM-DD`.
   *
   * Passed in rather than derived from `now`, because "per calendar day" means the person's
   * day and this module has no time zone. Deriving it from a UTC timestamp would reset the
   * counter at 02:00 in summer, which nobody would ever report as a bug and everybody would
   * find mildly wrong.
   */
  localDay: string;
};

/** Quiet hours, honouring a range that wraps past midnight (22 → 8 is the default). */
export function isQuiet(hour: number, from: number | null, to: number | null): boolean {
  if (from === null || to === null) return false;
  if (from === to) return false;
  return from < to ? hour >= from && hour < to : hour >= from || hour < to;
}

export function metresBetween(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): number {
  return greatCircleDistance([a.lat, a.lon], [b.lat, b.lon], UNITS.m);
}

/**
 * Which hot cells, if any, are worth a notification right now.
 *
 * The rules are checked cheapest-and-broadest first: the ones that reject the whole call
 * (disabled, quiet hours, the global cooldown, the daily cap) before the ones that reject a
 * single cell. On a phone this runs on every position update, so the common answer — nothing
 * — should cost almost nothing.
 */
export function decide(input: GeofenceInput): { alerts: Alert[]; state: GeofenceState } {
  // The counter belongs to a day; crossing midnight resets it before anything else looks at it.
  const state: GeofenceState =
    input.state.day === input.localDay
      ? input.state
      : { ...input.state, day: input.localDay, countToday: 0 };

  if (!input.settings.enabled) return { alerts: [], state };
  if (isQuiet(input.localHour, input.settings.quietFrom, input.settings.quietTo)) {
    return { alerts: [], state };
  }
  if (state.countToday >= input.settings.dailyCap) return { alerts: [], state };
  if (state.lastAlertAt !== null && input.now - state.lastAlertAt < ANY_ALERT_COOLDOWN_MS) {
    return { alerts: [], state };
  }

  // The movement threshold. Without it somebody who *lives* in a hotspot is warned about
  // their own street every six hours, which is the fastest way to have notifications turned
  // off for good. GPS noise alone does not cover 250 m.
  if (state.lastAlertPosition !== null) {
    if (metresBetween(state.lastAlertPosition, input.position) < MOVEMENT_THRESHOLD_M) {
      return { alerts: [], state };
    }
  }

  const candidates = input.hotCells
    .filter((hot) => hot.gdi >= input.settings.threshold)
    .filter((hot) => hot.sightingCount >= MIN_SIGHTINGS)
    .filter((hot) => isNear(input.currentCell, hot.cell, ALERT_RING))
    .filter((hot) => {
      const last = state.perCell[hot.cell];
      return last === undefined || input.now - last >= SAME_CELL_COOLDOWN_MS;
    })
    // Busiest first, so if two qualify the one worth interrupting for wins.
    .sort((a, b) => b.gdi - a.gdi);

  const winner = candidates[0];
  if (winner === undefined) return { alerts: [], state };

  const alert: Alert = {
    cell: winner.cell,
    gdi: winner.gdi,
    sightingCount: winner.sightingCount,
    lastSightingAt: winner.lastSightingAt,
    at: input.now,
  };

  return {
    alerts: [alert],
    state: {
      lastAlertAt: input.now,
      lastAlertPosition: input.position,
      perCell: { ...state.perCell, [winner.cell]: input.now },
      day: input.localDay,
      countToday: state.countToday + 1,
    },
  };
}

/**
 * The notification text (brief §10).
 *
 * `place` comes from reverse geocoding in the *client* and is allowed to be missing — the
 * server has no coordinate to geocode and that is not going to change.
 */
export function describeAlert(alert: Alert, place: string | undefined, now: number): string {
  const parts = [place, `GDI ${alert.gdi}`, `${alert.sightingCount} Meldungen`].filter(
    (part): part is string => part !== undefined && part !== '',
  );
  if (alert.lastSightingAt === null) return parts.join(' — ');

  const minutes = Math.max(0, Math.round((now - alert.lastSightingAt) / MINUTE));
  const ago =
    minutes < 60
      ? `vor ${minutes} Minuten`
      : minutes < 24 * 60
        ? `vor ${Math.round(minutes / 60)} Stunden`
        : `vor ${Math.round(minutes / (24 * 60))} Tagen`;
  return `${parts.join(' — ')}. Zuletzt ${ago}.`;
}

/** Clamp settings from an untrusted source into the ranges brief §10 allows. */
export function normaliseAlertSettings(input: unknown): AlertSettings {
  const raw = (input ?? {}) as Partial<Record<keyof AlertSettings, unknown>>;
  const hour = (value: unknown, fallback: number | null): number | null => {
    if (value === null) return null;
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 23) {
      return fallback;
    }
    return value;
  };
  const bounded = (value: unknown, low: number, high: number, fallback: number): number => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
    return Math.min(high, Math.max(low, Math.round(value)));
  };

  return {
    enabled: typeof raw.enabled === 'boolean' ? raw.enabled : DEFAULT_ALERT_SETTINGS.enabled,
    threshold: bounded(
      raw.threshold,
      THRESHOLD_RANGE.min,
      THRESHOLD_RANGE.max,
      DEFAULT_ALERT_SETTINGS.threshold,
    ),
    dailyCap: bounded(
      raw.dailyCap,
      DAILY_CAP_RANGE.min,
      DAILY_CAP_RANGE.max,
      DEFAULT_ALERT_SETTINGS.dailyCap,
    ),
    quietFrom: hour(raw.quietFrom, DEFAULT_ALERT_SETTINGS.quietFrom),
    quietTo: hour(raw.quietTo, DEFAULT_ALERT_SETTINGS.quietTo),
  };
}
